import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import './style.css';
import { postRequestApiByTokenWithDataType } from '../../api';
import chatLogo from '../../assets/images/logo/chatbot-icon.png';

const AiChatSystem = () => {
  // Redux state for user ID
  const { user_id } = useSelector(state => state.commonReducer);

  // State management
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedDocTypes, setSelectedDocTypes] = useState([]); // Array for multi-select support
  const [showConfirm, setShowConfirm] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Component to render structured messages
  const MessageRenderer = ({ message }) => {
    if (message.isBot) {
      return (
        <div className="bot-response-card">
          {/* <div className="bot-avatar">
            <img src={chatLogo} alt="IPEans AI" />
          </div> */}
          <div className="bot-content">
            {message.isError ? (
              <div className="error-message">
                <div className="error-icon">⚠️</div>
                <p>{message.text}</p>
              </div>
            ) : (
              <div className="structured-response">
                <div className="response-header">
                  <div className="response-meta">
                    <span className="response-time">{message.timestamp}</span>
                    {/* {message.sessionId && (
                      <span className="session-id">Session: {message.sessionId}</span>
                    )} */}
                  </div>
                </div>
                <div className="response-content">
                  <div className="answer-text">
                    {message.text.split('\n').map((line, index) => (
                      <p key={index}>{line}</p>
                    ))}
                  </div>
                </div>
                {/* Removed response footer with original question */}
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <div className="user-message-container">
          <div className="user-bubble">
            <div className="user-text">{message.text}</div>
            <div className="user-timestamp">{message.timestamp}</div>
          </div>
        </div>
      );
    }
  };

  // Generate unique session ID
  const generateSessionId = () => {
    const now = new Date();
    const timestamp = now.getFullYear().toString().slice(-2) + 
                     (now.getMonth() + 1).toString().padStart(2, '0') + 
                     now.getDate().toString().padStart(2, '0') + 
                     now.getHours().toString().padStart(2, '0') + 
                     now.getMinutes().toString().padStart(2, '0');
    return `sess_${timestamp}`;
  };

  // Initialize session ID on component mount
  useEffect(() => {
    setSessionId(generateSessionId());
  }, []);
  // Scroll to bottom when messages change
  useEffect(() => {
    const container = document.getElementById('chat-messages-container');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleTabClick = (tab) => {
    // Toggle selection for multi-select support
    setSelectedDocTypes(prev => {
      if (prev.includes(tab)) {
        // Remove if already selected
        return prev.filter(type => type !== tab);
      } else {
        // Add if not selected
        return [...prev, tab];
      }
    });
  };

  const handleNewChat = () => {
    if (inputValue.trim()) {
      setShowConfirm(true);
    } else {
      clearChat();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setInputValue('');
    setShowConfirm(false);
    setSelectedDocTypes([]); // Reset selected document types
    setSessionId(generateSessionId()); // Generate new session ID
  };

  const cancelNewChat = () => {
    setShowConfirm(false);
  };

  // Determine document type based on selected types
  const getDocType = () => {
    // If nothing selected OR both selected, return null (search all)
    if (selectedDocTypes.length === 0 || selectedDocTypes.length === 2) {
      return null;
    }
    
    const docTypeMap = {
      'project': 'document',
      'other': 'other_document'
    };
    
    // Return the single selected document type (not an array)
    const selectedType = selectedDocTypes[0];
    return docTypeMap[selectedType] || null;
  };

  const handleSendMessage = async (question) => {
    if (!user_id) {
      console.error('User ID not found');
      return null;
    }

    const docTypes = getDocType();
    const reqData = {
      session_id: sessionId,
      question: question,
      doc_type: docTypes,
      user_id: user_id
    };

    console.log('Sending API request with data:', reqData);

    try {
      setIsLoading(true);
      const res = await postRequestApiByTokenWithDataType('json', 'chatbot/ask', reqData);
      console.log('handleSendMessage Response:', res);
      return res;
    } catch (error) {
      console.error('Error sending message:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (inputValue.trim() === '' || isLoading) return;

    const userQuestion = inputValue.trim();
    
    // Add user message to chat
    const userMessage = {
      id: Date.now(),
      text: userQuestion,
      isBot: false,
      timestamp: new Date().toLocaleTimeString(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Call the API
    const response = await handleSendMessage(userQuestion);
    
    if (response && response.statusCode === 200) {
      // Add bot response to chat with structured data
      const botMessage = {
        id: Date.now() + 1,
        text: response.answer,
        isBot: true,
        timestamp: new Date().toLocaleTimeString(),
        sessionId: response.session_id,
        question: response.question,
        fullResponse: response // Store full response for debugging
      };
      setMessages(prev => [...prev, botMessage]);
    } else {
      // Handle error case
      const errorMessage = {
        id: Date.now() + 1,
        text: "Sorry, I couldn't process your request at the moment. Please try again.",
        isBot: true,
        timestamp: new Date().toLocaleTimeString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };
  

  return (
    <><div className="container-fluid">
      <div className="row">
    <div className="ai-chat-panel">
      {/* Header Row */}
      <div className="chat-header-row">
        <h1 className="chat-title">IPEans AI</h1>
        <div className="header-controls">
          <div className="tab-buttons">
            <button 
              className={`tab-btn ${selectedDocTypes.includes('project') ? 'active' : ''}`}
              onClick={() => handleTabClick('project')}
            >
              Project Documents
              {selectedDocTypes.includes('project') && (
                <span className="selected-indicator">✓</span>
              )}
            </button>
            <button 
              className={`tab-btn ${selectedDocTypes.includes('other') ? 'active' : ''}`}
              onClick={() => handleTabClick('other')}
            >
              Other Documents
              {selectedDocTypes.includes('other') && (
                <span className="selected-indicator">✓</span>
              )}
            </button>
          </div>
          <button 
            className="new-chat-btn" 
            onClick={handleNewChat}
            title="New chat"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* Chat Section */}
      <div className="chat-section" id="chat-messages-container">
        {/* Session Info */}
        {/* {sessionId && (
          <div className="session-info">
            <small>
              Session: {sessionId} | 
              Document Types: {
                selectedDocTypes.length === 0 
                  ? 'None selected (All documents)' 
                  : selectedDocTypes.length === 2
                  ? 'All selected (All documents)'
                  : selectedDocTypes.map(type => 
                      type === 'project' ? 'Project Docs' : 'Other Docs'
                    ).join(', ')
              }
            </small>
          </div>
        )} */}
        
        <div className="chat-messages">
          {messages.length === 0 && (
            <div className="welcome-message">
              <div className="welcome-avatar-only">
                <img src={chatLogo} alt="IPEans AI" />
              </div>
            </div>
          )}
          
          {messages.map((message) => (
            <div key={message.id} className={`message-wrapper ${message.isBot ? 'bot-message' : 'user-message'}`}>
              <MessageRenderer message={message} />
            </div>
          ))}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="message-wrapper bot-message">
              <div className="bot-response-card">
                {/* <div className="bot-avatar">
                  <img src={chatLogo} alt="IPEans AI" />
                </div> */}
                <div className="bot-content">
                  <div className="loading-dots">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Input Bar */}
      <div className="input-bar">
        <div className="input-container">
          <div className="ai-logo">
            <img src={chatLogo} alt="IPEans AI" />
          </div>
          <form onSubmit={handleSend} className="input-form">
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              placeholder="Ask IPEans AI anything"
              className="chat-input"
              disabled={isLoading}
            />
            <button type="submit" className="send-btn" disabled={isLoading || !inputValue.trim()}>
              {isLoading ? (
                <div className="loading-spinner"></div>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <p>You have unsaved input. Start a new chat anyway?</p>
            <div className="confirm-buttons">
              <button onClick={cancelNewChat} className="cancel-btn">Cancel</button>
              <button onClick={clearChat} className="confirm-btn">New Chat</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
    </div>
    </>
  );
};

export default AiChatSystem;
